/**
 * 源形态（SourceShape）类型契约（v1）。
 * 依据：docs/05-连接数据.md §4「共享契约」。
 * 源形态回答「对方 JSON 怎么拆」，随场景 / .f3p 走，**不含**密钥与实例参数值。
 * 密钥只出现在 LocalConnectionConfig（编辑器本机调试）与 RuntimeSessionAuth（运行画面本机会话）。
 * 曾用名 TemplateConfig，自 2026-08 起统一为 SourceShape，旧名只作 deprecated 别名。
 */

import type { DeviceSnapshot } from './realtime'

/** 路径表达式：`a.b.c` / `arr[0].field`；信封为空时用 `""` 或 `"$"`。不支持通配/切片/函数。 */
export type PathExpr = string

/** GET /api/live 响应体：复用现有 DeviceSnapshot，禁止另造快照结构。 */
export type LiveApiResponse = DeviceSnapshot[]

export type SourceAdapterDeployment = 'gateway-required' | 'browser-direct'

export type SourceAdapterAuthMode = 'static-token' | 'login-token'

export type PaginationStrategy = 'none' | 'fixed-page-size' | 'loop-by-total'

export type TimestampFallback = 'now' | 'max-of-mapped-fields'

export interface SuccessCheckConfig {
  codePath: PathExpr
  successValues: (string | number)[]
  /** 命中则触发登录换 token 重试 */
  authFailValues?: (string | number)[]
}

/**
 * 部分系统登录后还需二次「选择机构」才能换到最终 token（如辰和 MES：
 * POST /auth/loginWithoutCode 拿到临时 token + 机构列表 → PUT /auth/chooseOrganization 换最终 token）。
 * 对照 chenhe-mes-api-docs skill 的 mes-login.mjs 实测固化。
 */
export interface ChooseOrganizationConfig {
  path: string
  method: 'PUT' | 'POST'
  /** 第一步响应里「机构列表」的路径 */
  organizationListResponsePath: PathExpr
  /** 机构列表项里，用于匹配 connectionOrgParam 值的字段名（如 "organizationId"） */
  organizationIdField: string
  /** 机构列表项里 uuid 字段名，匹配到后填入第二步请求体 */
  organizationUuidField: string
  /** 第二步请求体里，目标机构 uuid 对应的字段名 */
  requestOrgField: string
  /** 第二步响应里最终 token 的路径 */
  tokenResponsePath: PathExpr
  /** connection.queryParamValues 里用哪个参数的值去匹配 organizationIdField */
  connectionOrgParam: string
}

export interface LoginAuthConfig {
  path: string
  method: 'POST'
  /** 请求体里账号/密码对应的字段名，不含字面值 */
  bodyFields: { username: string; password: string }
  /** 固定附加字段（不含密钥），如辰和 MES 的 { appKey: 'org' } */
  extraBodyFields?: Record<string, string | number | boolean>
  tokenResponsePath: PathExpr
  /** 登录/选择机构失败时，响应体里错误信息的路径，用于报错提示（如 "msg"） */
  errorMessagePath?: PathExpr
  /** 需要二次选择机构才能拿到最终 token 时填写 */
  chooseOrganization?: ChooseOrganizationConfig
}

export interface TemplateRequestConfig {
  method: 'GET' | 'POST'
  path: string
  /** 参数名清单；值来自 ConnectionInstanceConfig.queryParamValues */
  queryParams: string[]
  /** 需要附加的固定 header 名；值来自连接实例或常量 */
  headers?: string[]
  successCheck: SuccessCheckConfig
}

export interface TemplateAuthConfig {
  mode: SourceAdapterAuthMode
  /** mode = login-token 时声明「怎么登录」，不含账号密码字面值 */
  login?: LoginAuthConfig
}

export interface PaginationConfig {
  strategy: PaginationStrategy
  pageSizeParam?: string
  pageNumParam?: string
  totalPath?: PathExpr
  /** strategy = fixed-page-size 时用 */
  fixedPageSize?: number
}

export interface IdentityConfig {
  assetCodeSourcePath: PathExpr
  deviceNameSourcePath?: PathExpr
  /** 用于 fieldMappings 按机型分组匹配 */
  deviceTypeSourcePath?: PathExpr
}

export interface FieldStalenessConfig {
  /** 默认 false；只对应高频更新的实时读数开启 */
  enabled: boolean
  sourceTimePath?: PathExpr
  thresholdMs?: number
}

export type DeviceTypeMatch =
  | { path: PathExpr; equals: string }
  | 'default'

export interface FieldMapping {
  /**
   * 按机型分组：不匹配则跳过本条。
   * 引擎按「先精确匹配、再退 default」处理；default 条目须排在同 targetField 的机型特定条目之后才生效。
   */
  deviceTypeMatch?: DeviceTypeMatch
  sourcePath: PathExpr
  /** 仅供映射确认界面显示中文名，不进最终 snapshot */
  sourceLabelPath?: PathExpr
  /** 落到 snapshot.metrics.<targetField> */
  targetField: string
  /** 默认 1；人工确认后才写非 1 */
  scale?: number
  /** 默认 0 */
  offset?: number
  /** 展示用，不参与换算 */
  unit?: string
  staleness?: FieldStalenessConfig
}

export type ConditionExpr =
  | { path: PathExpr; op: 'equals'; value: string | number | boolean }
  | { path: PathExpr; op: 'in'; values: (string | number)[] }
  | { path: PathExpr; op: 'gt' | 'gte' | 'lt' | 'lte'; value: number }
  | { path: PathExpr; op: 'exists' }

export interface StatusRuleResult {
  /** 落在 statusMapper 认识的词：offline / alarm / stopped / standby / running … */
  status: string
  statusLabel?: string
  /** 是否计入 alarms 数组 */
  alarm?: boolean
  alarmLabel?: string
}

export interface StatusRule {
  /** 数组顺序即优先级，命中第一条即停；同规则内条件为 AND。conditions 为空视为总是命中（兜底）。 */
  conditions: ConditionExpr[]
  result: StatusRuleResult
}

export interface TimestampConfig {
  sourcePath?: PathExpr
  fallback: TimestampFallback
}

/**
 * 源形态：回答「这类 API 长什么样、怎么解析」。
 * 可随 .f3p / 场景走；不含客户私密实例参数。
 * 命名锁定：字段名与含义见 docs/05-连接数据.md §4.2。
 */
export interface SourceShape {
  version: string
  templateId: string
  templateLabel: string
  deployment: SourceAdapterDeployment
  request: TemplateRequestConfig
  auth: TemplateAuthConfig
  pagination: PaginationConfig
  envelope: PathExpr
  jsonStringFields: PathExpr[]
  identity: IdentityConfig
  fieldMappings: FieldMapping[]
  statusRules: StatusRule[]
  timestamp: TimestampConfig
  /**
   * 设计态「拉一次」预览专用（P5-C 发现的口子）：点位字典容器在单条设备记录里的路径，
   * 里面每一项形如 `{ name, value, time? }`（键名约定与 MES 的 attr1 相同）。
   * 解析顺序见 `resolvePointsContainerPath`（显式路径 → jsonStringFields[0] → attr1）。
   * P0/P1/P2 运行时引擎不直接读本字段——只影响 preview / 登记 fieldMappings 的点位发现；
   * 运行时靠 fieldMappings.sourcePath 取数。点位子对象键名若不是 name/value/time，
   * 本字段也救不了，只能手写 fieldMappings（便利功能边界，非运行时限制）。
   */
  previewPointsContainerPath?: PathExpr
}

/**
 * 点位字典容器路径（预览抽点与 ensureDeviceFieldMappings 登记 sourcePath 必须同一套）。
 * 顺序：`previewPointsContainerPath` → `jsonStringFields[0]` → `'attr1'`。
 * 禁止两处各写一套回退（否则 jsonStringFields 顺序与客户模板不一致时会静默绑错容器）。
 */
export function resolvePointsContainerPath(
  shape: Pick<SourceShape, 'previewPointsContainerPath' | 'jsonStringFields'>,
): string {
  const explicit = shape.previewPointsContainerPath
  if (explicit != null && String(explicit).trim() !== '') return String(explicit).trim()
  const fromJson = shape.jsonStringFields?.[0]
  if (fromJson != null && String(fromJson).trim() !== '') return String(fromJson).trim()
  return 'attr1'
}

/**
 * 连接实例：回答「这次连的是谁、用谁的身份」。
 * 仅网关主机本地配置，永不进 SceneData / .f3p。
 */
export interface ConnectionInstanceConfig {
  templateId: string
  baseUrl: string
  queryParamValues: Record<string, string | number>
  /** template.request.headers / auth 二次请求里声明的固定 header 名对应的值（如 Content-Type） */
  headerValues?: Record<string, string>
  credentials?: { username: string; password: string }
  staticToken?: string
  pollIntervalMs: number
  cacheTtlMs?: number
}

/**
 * @deprecated 旧名，指向 SourceShape（docs/05-连接数据.md §4.2）。新代码一律用 SourceShape；
 * 仅在迁移期内保留此别名，清零后删除。
 */
export type TemplateConfig = SourceShape
